/**
 * Client-safe: hydrate offloaded timeline bodies and turn them into plaintext
 * excerpts suitable for pasting into an AI prompt field (Solution Engineering).
 */

import { buildCommunicationPreviewForModel } from "@/lib/core/ai/summary-communication-preview";
import {
  AI_SOLUTION_ENGINEERING_NOTE_CHARS,
  AI_SOLUTION_ENGINEERING_NOTE_TAKE,
} from "@/lib/core/ai/summary-payload-constants";

export type TimelineNoteSource = {
  id: string;
  title?: string | null;
  body?: string | null;
  body_s3_key?: string | null;
  occurred_at: string;
  type?: string;
};

async function fetchFullBodyIfNeeded(item: TimelineNoteSource): Promise<string> {
  const inline = typeof item.body === "string" ? item.body.trim() : "";
  const key = typeof item.body_s3_key === "string" ? item.body_s3_key.trim() : "";
  // List body is preview when offloaded — don't treat it as the full inline body.
  if (key) {
    try {
      const res = await fetch(`/api/communications/${item.id}/body`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = (await res.json()) as { body?: string | null };
        if (typeof data.body === "string" && data.body.trim()) {
          return data.body.trim();
        }
      }
    } catch {
      // Fall through to preview.
    }
    return inline;
  }
  return inline;
}

/**
 * Newest-first timeline rows → dated plaintext lines for AI forms.
 * Caps row count and per-body chars so the pain-points field stays token-cheap.
 */
export async function extractTimelineNotesForAi(
  items: TimelineNoteSource[],
  options?: { take?: number; maxPlainChars?: number }
): Promise<string> {
  const take = options?.take ?? AI_SOLUTION_ENGINEERING_NOTE_TAKE;
  const maxPlainChars = options?.maxPlainChars ?? AI_SOLUTION_ENGINEERING_NOTE_CHARS;

  const slice = items.slice(0, take);
  const lines: string[] = [];

  for (const item of slice) {
    const raw = await fetchFullBodyIfNeeded(item);
    const { preview } = buildCommunicationPreviewForModel(raw, maxPlainChars);
    if (!preview.trim()) continue;
    const when = new Date(item.occurred_at).toLocaleDateString();
    const title = item.title?.trim() || "Note";
    lines.push(`[${when}] ${title}: ${preview}`);
  }

  return lines.join("\n\n");
}
